/**
 * SectionPanel — Expandable section panel (Problem / Solution / Market Signal)
 * Authority: DEAL_EVALUATOR_PAGE_SPEC.md Section 5.6
 * Design: DS-CARD-V5 SECTION_PANEL + VERDICT_PILL + RATIONALE_BLOCK tokens — zero inline hex.
 *
 * Props:
 *   - section     : { prose, provisional_verdict, q1_answer, q2_answer, modifiers_applied, version }
 *   - sectionKey  : 'problem' | 'solution' | 'market_signal'
 *   - onRerun     : (sectionKey, modifiers) => void
 *   - rerunning   : bool — shows loading state while section rerun is in progress
 */

import React, { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import {
  SECTION_PANEL,
  VERDICT_PILL,
  RATIONALE_BLOCK,
  DS_TYPOGRAPHY,
  DS_COLORS,
  KPI_TILE,
} from '@/config/ui-invariants';
import { AGENT_CONFIG } from '@/config/task-display-config';
import KnobsPanel from './KnobsPanel';

const dealConfig = AGENT_CONFIG.deal_evaluator;

function VerdictPill({ verdict }) {
  const tokens = VERDICT_PILL[verdict] || VERDICT_PILL.unclear;
  const shape = VERDICT_PILL.pill;

  return (
    <span style={{
      background: tokens.background,
      color: tokens.text,
      border: `${shape.borderWidth} solid ${tokens.border}`,
      borderRadius: shape.borderRadius,
      fontSize: shape.fontSize,
      fontWeight: shape.fontWeight,
      padding: `${shape.paddingTop} ${shape.paddingRight} ${shape.paddingBottom} ${shape.paddingLeft}`,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      letterSpacing: DS_TYPOGRAPHY.verdictPillText.letterSpacing,
      textTransform: DS_TYPOGRAPHY.verdictPillText.textTransform,
    }}>
      <span style={{
        width: tokens.dotSize,
        height: tokens.dotSize,
        borderRadius: '50%',
        background: tokens.dot,
        display: 'inline-block',
        flexShrink: 0,
      }} />
      {tokens.label}
    </span>
  );
}

export default function SectionPanel({ section, sectionKey, onRerun, rerunning }) {
  const [proseExpanded, setProseExpanded] = useState(false);
  const [knobsOpen, setKnobsOpen] = useState(false);
  const [knobValues, setKnobValues] = useState({
    scrutiny: section?.modifiers_applied?.scrutiny ?? 0,
    research_depth: section?.modifiers_applied?.research_depth ?? 0,
    india_lens: section?.modifiers_applied?.india_lens ?? 0,
  });

  const verdict = section?.provisional_verdict || 'unclear';
  const label = dealConfig.sectionLabels[sectionKey] || sectionKey;
  const leftBorder = SECTION_PANEL.leftBorderByVerdict[verdict] || SECTION_PANEL.leftBorderByVerdict.unclear;
  const bodyWash = SECTION_PANEL.bodyWash[verdict] || SECTION_PANEL.bodyWash.unclear;
  const questions = KPI_TILE.sectionQuestions[sectionKey] || {};
  const rationaleTokens = RATIONALE_BLOCK.byVerdict[verdict] || RATIONALE_BLOCK.byVerdict.unclear;

  const prose = section?.prose || '';
  const proseLines = prose.split('\n');
  const truncatedProse = proseLines.slice(0, 3).join('\n');
  const needsTruncation = proseLines.length > 3;

  const handleKnobChange = (key, value) => {
    setKnobValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleRerunSection = () => {
    onRerun?.(sectionKey, knobValues);
  };

  return (
    <div
      style={{
        ...SECTION_PANEL.shell,
        borderLeft: `4px solid ${leftBorder}`,
        marginBottom: '14px',
        overflow: 'hidden',
      }}
      className="deal-screen-section-panel"
    >
      {/* Header row */}
      <div style={{
        ...SECTION_PANEL.headerRow,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ ...DS_TYPOGRAPHY.sectionTitle, textTransform: 'none', color: DS_COLORS.text.primary }}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {section?.version > 1 && (
            <span style={{
              ...SECTION_PANEL.versionBadge,
              ...DS_TYPOGRAPHY.selectorBtn,
            }}>
              v{section.version}
            </span>
          )}
          <VerdictPill verdict={verdict} />
        </div>
      </div>

      {/* Body */}
      <div style={{
        padding: SECTION_PANEL.bodyPadding,
        background: bodyWash,
      }}>
        {/* Prose */}
        {prose && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{
              ...DS_TYPOGRAPHY.bodySm,
              color: DS_COLORS.text.body,
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}>
              {proseExpanded || !needsTruncation ? prose : truncatedProse + '...'}
            </p>
            {needsTruncation && (
              <button
                onClick={() => setProseExpanded(!proseExpanded)}
                style={{
                  ...DS_TYPOGRAPHY.actionBtn,
                  color: DS_COLORS.text.brand,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 0',
                  marginTop: '4px',
                }}
              >
                {proseExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}

        {/* Q&A blocks */}
        {(section?.q1_answer || section?.q2_answer) && (
          <div style={{
            ...RATIONALE_BLOCK.shell,
            background: rationaleTokens.background,
            borderColor: rationaleTokens.border,
          }}>
            {section?.q1_answer && (
              <div style={{ marginBottom: section?.q2_answer ? '10px' : 0 }}>
                <p style={{
                  ...DS_TYPOGRAPHY.monoLabel,
                  color: DS_COLORS.text.muted,
                  margin: `0 0 ${RATIONALE_BLOCK.sectionLabel.marginBottom}`,
                }}>
                  {questions.q1 || 'Question 1'}
                </p>
                <p style={{
                  ...DS_TYPOGRAPHY.bodySm,
                  color: RATIONALE_BLOCK.answerLine1.color,
                  margin: 0,
                }}>
                  {section.q1_answer}
                </p>
              </div>
            )}
            {section?.q2_answer && (
              <div>
                <p style={{
                  ...DS_TYPOGRAPHY.monoLabel,
                  color: DS_COLORS.text.muted,
                  margin: `0 0 ${RATIONALE_BLOCK.sectionLabel.marginBottom}`,
                }}>
                  {questions.q2 || 'Question 2'}
                </p>
                <p style={{
                  ...DS_TYPOGRAPHY.bodySm,
                  color: RATIONALE_BLOCK.answerLine2.color,
                  margin: 0,
                }}>
                  {section.q2_answer}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Knobs footer */}
      <div style={{
        ...SECTION_PANEL.adjustFooter,
      }} className="deal-screen-knobs-footer">
        <button
          onClick={() => setKnobsOpen(!knobsOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            ...DS_TYPOGRAPHY.actionBtn,
            color: DS_COLORS.text.secondary,
            padding: 0,
            minHeight: '44px',
            width: '100%',
          }}
        >
          Adjust analysis
          <CaretDown
            weight="bold"
            style={{
              width: '14px',
              height: '14px',
              transition: 'transform 0.15s ease',
              transform: knobsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          />
        </button>
        {knobsOpen && (
          <div style={{ marginTop: '12px' }}>
            <KnobsPanel
              values={knobValues}
              onChange={handleKnobChange}
              disabled={rerunning}
            />
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleRerunSection}
                disabled={rerunning}
                style={{
                  ...DS_TYPOGRAPHY.actionBtn,
                  background: rerunning ? DS_COLORS.surface.slate : DS_COLORS.surface.white,
                  color: DS_COLORS.text.brand,
                  border: `1.5px solid ${DS_COLORS.border.brand}`,
                  borderRadius: '8px',
                  minHeight: '44px',
                  padding: '8px 20px',
                  cursor: rerunning ? 'wait' : 'pointer',
                  opacity: rerunning ? 0.6 : 1,
                }}
              >
                {rerunning ? 'Re-running...' : 'Re-run this section'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
